import React, { useEffect, useMemo, useState } from 'react';
import { X, ExternalLink, Loader2 } from 'lucide-react';
import type { PlanReview } from '../../types';
import { getPlanReviewById, listAnnotationsForPlanReview } from '../../utils/planReviewService';
import { groupPlanReviewAnnotationsByPage } from '../../utils/planReviewSummary';
import RichCommentRenderer from '../pm/RichCommentRenderer';
import PlanReviewSummaryPreview from './PlanReviewSummaryPreview';
import './PlanReviewCompleteModal.css';

export interface PlanReviewViewModalProps {
  reviewId: string;
  onClose: () => void;
  onOpenDrawing?: (review: PlanReview) => void;
}

export default function PlanReviewViewModal({
  reviewId,
  onClose,
  onOpenDrawing,
}: PlanReviewViewModalProps) {
  const [review, setReview] = useState<PlanReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const loaded = await getPlanReviewById(reviewId);
        if (cancelled) return;
        if (!loaded) {
          setError('This plan review could not be found.');
          setReview(null);
          return;
        }
        setReview(loaded);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Could not load plan review');
          setReview(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewId]);

  const [annotations, setAnnotations] = useState<Awaited<ReturnType<typeof listAnnotationsForPlanReview>>>([]);
  const [annotationsLoading, setAnnotationsLoading] = useState(false);

  useEffect(() => {
    if (!review?.id) {
      setAnnotations([]);
      return;
    }
    let cancelled = false;
    setAnnotationsLoading(true);
    void listAnnotationsForPlanReview(review.id).then((anns) => {
      if (!cancelled) {
        setAnnotations(anns);
        setAnnotationsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [review?.id]);

  const commentGroups = useMemo(
    () => groupPlanReviewAnnotationsByPage(annotations),
    [annotations],
  );

  const completerName = review?.completedByName || review?.startedByName || 'Someone';
  const completedAt = review?.completedAt || review?.startedAt;

  return (
    <div className="plan-review-complete-overlay" onClick={onClose} role="presentation">
      <div
        className="plan-review-complete-modal plan-review-view-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="plan-review-view-title"
      >
        <header className="plan-review-complete-header">
          <div>
            <h2 id="plan-review-view-title">Plan review</h2>
            <p className="plan-review-complete-sub">
              {review?.fileName || 'Drawing'}
              {completedAt ? ` · completed ${completedAt.toLocaleDateString()}` : ''}
            </p>
          </div>
          <button type="button" className="plan-review-complete-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {loading ? (
          <p className="plan-review-view-status">
            <Loader2 size={16} aria-hidden /> Loading review…
          </p>
        ) : error ? (
          <p className="plan-review-complete-error">{error}</p>
        ) : review ? (
          <>
            <p className="plan-review-view-meta">
              Reviewed by <strong>{completerName}</strong>
              {review.markupStrokeCount ? (
                <>
                  {' '}
                  · {review.markupStrokeCount} markup stroke
                  {review.markupStrokeCount === 1 ? '' : 's'}
                </>
              ) : null}
            </p>

            {review.summaryNoteHtml || review.summaryNote ? (
              <section className="plan-review-complete-section">
                <h3 className="plan-review-complete-label">Cover note</h3>
                <div className="plan-review-view-note">
                  <RichCommentRenderer
                    bodyHtml={review.summaryNoteHtml}
                    body={review.summaryNote || ''}
                    enlarged
                  />
                </div>
              </section>
            ) : null}

            <section className="plan-review-complete-section plan-review-view-comments" aria-labelledby="plan-review-view-preview-heading">
              <h3 id="plan-review-view-preview-heading" className="plan-review-complete-label">
                Comments in this review
              </h3>
              {annotationsLoading ? (
                <p className="plan-review-view-status">Loading comments…</p>
              ) : (
                <PlanReviewSummaryPreview commentGroups={commentGroups} />
              )}
            </section>

            {onOpenDrawing ? (
              <footer className="plan-review-view-footer">
                <button
                  type="button"
                  className="plan-review-complete-primary plan-review-view-open-drawing"
                  onClick={() => onOpenDrawing(review)}
                >
                  <ExternalLink size={14} aria-hidden />
                  Open drawing
                </button>
              </footer>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
